import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import moment from "moment";
import { css } from "../../../../styled-system/css";
import { Box, HStack, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../core/basic/Button";
import { RT8ColumnSelector } from "../../core/blocks/MetricSelector";
import { SearchInput } from "../../fields/base/TextField";
import { RT8FilterWidget } from "../../../modules/merchandising/components/tables/FilterWidget";
import { createAppColumnHelper, useClientTable } from "../../table/tableFeatures";
import { usePersistedTableState } from "../../table/usePersistedTableState";
import { commaSearchFilterFn, customFilterFn } from "../../table/filterFns";
import { resetTable } from "../../table/utils";
import { RT8Pagination } from "../../table/Pagination";
import { Table } from "../../table/Table";
import { MetricCell } from "../../table/cells/MetricCell";
import getMetricSpec from "../../../modules/merchandising/utils/metrics";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import AnnotationCell from "./AnnotationCell";
import { AnnotationExpander } from "./AnnotationExpander";

const columnHelper = createAppColumnHelper();

/** An annotation's stats hold a single entry, keyed by entity, of before/after daily values. */
const getDailyStat = (annotation, statsKey) => {
  const stats = annotation.stats ?? {};
  return stats[Object.keys(stats)[0]]?.[statsKey];
};

/** What the Events cell shows as its title. */
const getTitle = (annotation) =>
  annotation.advertisement?.name ?? annotation.product?.product_title ?? annotation.description ?? undefined;

const getCreatedByName = ({ created_by: createdBy }) =>
  createdBy ? `${createdBy.first_name} ${createdBy.last_name}` : undefined;

const annotationSearchFilterFn = commaSearchFilterFn((annotation) => [
  annotation.advertisement?.name ?? "",
  annotation.product?.product_title ?? "",
  annotation.product?.asin ?? "",
  annotation.product?.sku ?? "",
  annotation.description ?? "",
  getCreatedByName(annotation) ?? "",
]);

// `metric` is the registered metric whose formatting the column borrows; the values come from the annotation's stats.
const PRODUCT_METRICS = [
  { id: "sales", metric: "sales", statsKey: "daily_total_sales", label: ($) => $.annotations["Daily Total Sales"] },
  {
    id: "page_views",
    metric: "page_views",
    statsKey: "daily_page_views",
    label: ($) => $.annotations["Daily Page Views"],
  },
  { id: "bsr_rank", metric: "bsr", statsKey: "daily_bsr_rank", label: ($) => $.annotations["Daily BSR Rank"] },
];

const AD_METRICS = [
  { id: "acos", metric: "acos", statsKey: "daily_acos", label: ($) => $.annotations["Daily ACOS"] },
  {
    id: "ad_sales",
    metric: "ad_sales",
    statsKey: "daily_attributed_sales_7d",
    label: ($) => $.annotations["Daily Ad Sales"],
  },
  { id: "ad_spend", metric: "ad_spend", statsKey: "daily_cost", label: ($) => $.annotations["Daily Ad Spend"] },
  {
    id: "impressions",
    metric: "impressions",
    statsKey: "daily_impressions",
    label: ($) => $.annotations["Daily Impressions"],
  },
  { id: "clicks", metric: "clicks", statsKey: "daily_clicks", label: ($) => $.annotations["Daily Clicks"] },
  {
    id: "conversion_rate",
    metric: "conversion_rate",
    statsKey: "daily_cr",
    label: ($) => $.annotations["Daily Conv. Rate"],
  },
];

function AnnotationsTableContent({ annotations, isLoading, annotationType }) {
  const { t } = useTranslation(["annotations", "common"]);
  const dateFmt = useDateFormatter("medium");
  const isProduct = annotationType === "product";

  const columns = useMemo(
    () => [
      columnHelper.accessor(getTitle, {
        id: "event",
        header: t(($) => $.annotations["Events"]),
        size: 450,
        minSize: 300,
        maxSize: 700,
        enableHiding: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        cell: ({ row }) => <AnnotationCell row={row.original} />,
      }),
      columnHelper.accessor((annotation) => annotation.ts.slice(0, 10), {
        id: "date",
        header: t(($) => $.annotations["Date"]),
        size: 130,
        enableResizing: false,
        enableColumnFilter: false,
        sortFn: "alphanumeric",
        cell: ({ getValue }) => dateFmt.format(moment(getValue()).toDate()),
      }),
      ...[...(isProduct ? PRODUCT_METRICS : []), ...AD_METRICS].map(({ id, metric, statsKey, label }) => {
        const spec = getMetricSpec(metric);
        return columnHelper.accessor((annotation) => getDailyStat(annotation, statsKey)?.after ?? undefined, {
          id,
          header: t(label),
          size: 160,
          minSize: 120,
          maxSize: 260,
          sortUndefined: "last",
          cell: ({ getValue, row }) => (
            <MetricCell
              value={getValue() ?? null}
              deltaValue={getDailyStat(row.original, statsKey)?.after_diff}
              formatOptions={{
                format: spec.format,
                // Daily values are averages, so counts such as page views and BSR rank round to whole numbers.
                decimalPlaces: spec.decimalPlaces ?? (spec.format === "number" ? 0 : undefined),
                compact: false,
              }}
              compareHighlight={spec.compareHighlight}
            />
          ),
          meta: {
            columnFilterOptions: { label: t(label), format: spec.format },
            justify: "end",
          },
        });
      }),
      ...(isProduct ?
        []
      : [
          columnHelper.accessor(getCreatedByName, {
            id: "created_by",
            header: t(($) => $.annotations["Created By"]),
            size: 180,
            sortFn: "alphanumeric",
            sortUndefined: "last",
          }),
        ]),
    ],
    [dateFmt, isProduct, t],
  );

  const { state, initialState, setters } = usePersistedTableState({
    persistKey: `${annotationType}AnnotationsTable`,
    initialState: {
      sorting: [{ id: "date", desc: true }],
      // Keeps the events first when the column selector reorders the other columns.
      columnPinning: { start: ["event"], end: [] },
    },
  });

  const table = useClientTable({
    data: annotations,
    columns,
    getRowId: (annotation) => String(annotation.id),
    // The expander charts the annotated product or ad; an annotation on neither has nothing to chart.
    getRowCanExpand: (row) => Boolean(row.original.product?.id ?? row.original.advertisement?.id),
    defaultColumn: { filterFn: customFilterFn },
    globalFilterFn: annotationSearchFilterFn,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: isLoading,
    },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              aria-label={t(($) => $.common.table["Reset table filters"])}
              onPress={() => resetTable(table)}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter ?? ""}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} renderExpander={(row) => <AnnotationExpander row={row} />} />
    </>
  );
}

export function AnnotationsTable({ data, isLoading, annotationType = "advertisement" }) {
  const { t } = useTranslation(["annotations"]);

  const annotations = useMemo(
    () =>
      data?.flatMap((group) => group.annotations).filter((annotation) => !annotationType || annotation[annotationType]),
    [data, annotationType],
  );

  if (!isLoading && !annotations?.length) {
    return (
      <Box textAlign="center" color="grey" paddingTop="xlarge" paddingBottom="large">
        {annotationType === "product" ?
          t(($) => $.annotations["There are no product annotations for the selected period"])
        : t(($) => $.annotations["There are no advertisement annotations for the selected period"])}
      </Box>
    );
  }

  // Each annotation type has its own columns and saved settings.
  return (
    <AnnotationsTableContent
      key={annotationType}
      annotations={annotations}
      isLoading={isLoading}
      annotationType={annotationType}
    />
  );
}
